import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  Settings, 
  List, 
  Terminal,
  Shield,
  HardDrive
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Components
import ClusterDashboard from '../components/ClusterDashboard';
import ClusterManagement from '../components/ClusterManagement';
import NodeServiceView from '../components/NodeServiceView';
import LiveTerminal from '../components/LiveTerminal';
import UserAccessRBAC from '../components/UserAccessRBAC';
import DeviceRegistrationManagement from '../components/DeviceRegistrationManagement';

export default function EdgeView({
  // User data
  user,
  
  // Cluster data
  clusters,
  clusterDetails,
  clusterHealth,
  serviceStatus,
  
  // Node data
  nodes,
  unassignedNodes,
  nodeTelemetry,
  
  // Application data
  applications,
  applicationDetails,
  
  // Services and pods
  services,
  pods,
  
  // Terminal state
  terminalOutput,
  terminalConnected,
  activeCluster,
  activeNamespace,
  
  // RBAC data
  rbacUsers,
  roles,
  
  // Device registration data (admin only)
  pendingDevices,
  
  // Loading states
  isFetching,
  isCreating,
  isDeleting,
  isUpdating,
  isInstalling,
  isExecutingCommand,
  isLoadingRbac,
  isLoadingDevices,
  
  // Error states
  errors,
  rbacError,
  deviceError,
  
  // Action creators
  fetchClusters,
  createCluster,
  deleteCluster,
  updateClusterStatus,
  fetchClusterDetails,
  fetchClusterHealth,
  fetchServiceStatus,
  fetchNodes,
  createNode,
  fetchNodeTelemetry,
  updateNodeStatus,
  deleteNode,
  assignNode,
  removeNode,
  transferNode,
  fetchUnassignedNodes,
  fetchApps,
  installApp,
  fetchAppDetails,
  updateAppStatus,
  deleteApp,
  scaleApp,
  restartApp,
  fetchServices,
  fetchPods,
  fetchPendingDevices,
  approveDevice,
  rejectDevice,
  setActiveCluster,
  setActiveNamespace,
  connectTerminal,
  disconnectTerminal,
  executeCommand,
  clearTerminalOutput,
  addTerminalOutput,
  fetchRbacUsers,
  inviteUser,
  updateUserRole,
  deleteUser,
  fetchRoles
}) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const isMobile = window.innerWidth < 768;

  // Determine if device registration tab should be shown (admin only)
  const showDeviceRegistration = user && user.currentUser && user.currentUser.user && user.currentUser.user.role === 'admin';

  const tabs = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      component: ClusterDashboard
    },
    {
      id: 'management',
      label: 'Cluster Management',
      icon: Settings,
      component: ClusterManagement
    },
    {
      id: 'nodes',
      label: 'Nodes & Services',
      icon: List,
      component: NodeServiceView
    },
    {
      id: 'terminal',
      label: 'Live Terminal',
      icon: Terminal,
      component: LiveTerminal
    },
    {
      id: 'rbac',
      label: 'User Access (RBAC)',
      icon: Shield,
      component: UserAccessRBAC
    }
  ];

  if (showDeviceRegistration) {
    tabs.push({
      id: 'devices',
      label: 'Device Registration',
      icon: HardDrive,
      component: DeviceRegistrationManagement
    });
  }

  return (
    <div className="relative flex flex-col">
      <Card className="p-4 rounded-none border-b border-gray-200 bg-white">
        <div className="container mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Edge Computing Platform
          </h1>
          <p className="text-gray-600">
            Manage your edge clusters, nodes, and applications
          </p>
        </div>
      </Card>

      <div className="flex-1 flex flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <div className="border-b border-gray-200 bg-white">
            <div className="container mx-auto px-4">
              <TabsList className="grid w-full grid-cols-5 lg:grid-cols-6 bg-transparent h-auto p-0">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className={cn(
                        "flex flex-col items-center gap-2 py-4 px-2 min-h-16 text-sm font-medium",
                        "data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600",
                        "data-[state=active]:border-b-2 data-[state=active]:border-blue-600",
                        isMobile && "text-xs px-1"
                      )}
                    >
                      <IconComponent className="w-5 h-5" />
                      <span className={cn(
                        "text-center leading-tight",
                        isMobile && "text-xs"
                      )}>
                        {tab.label}
                      </span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
          </div>

          <div className="flex-1">
            {tabs.map((tab) => {
              const Component = tab.component;
              return (
                <TabsContent 
                  key={tab.id} 
                  value={tab.id} 
                  className="m-0 p-4"
                >
                  <Component
                    // User data
                    user={user.currentUser}
                    
                    // Cluster data
                    clusters={clusters}
                    clusterDetails={clusterDetails}
                    clusterHealth={clusterHealth}
                    serviceStatus={serviceStatus}
                    
                    // Node data
                    nodes={nodes}
                    unassignedNodes={unassignedNodes}
                    nodeTelemetry={nodeTelemetry}
                    
                    // Application data
                    applications={applications}
                    applicationDetails={applicationDetails}
                    
                    // Services and pods
                    services={services}
                    pods={pods}
                    
                    // Terminal state
                    terminalOutput={terminalOutput}
                    terminalConnected={terminalConnected}
                    activeCluster={activeCluster}
                    activeNamespace={activeNamespace}
                    
                    // RBAC data
                    rbacUsers={rbacUsers}
                    roles={roles}
                    
                    // Device registration data (admin only)
                    pendingDevices={pendingDevices}
                    
                    // Loading states
                    isFetching={isFetching}
                    isCreating={isCreating}
                    isDeleting={isDeleting}
                    isUpdating={isUpdating}
                    isInstalling={isInstalling}
                    isExecutingCommand={isExecutingCommand}
                    isLoadingRbac={isLoadingRbac}
                    isLoadingDevices={isLoadingDevices}
                    
                    // Error states
                    errors={errors}
                    rbacError={rbacError}
                    deviceError={deviceError}
                    
                    // Action creators
                    fetchClusters={fetchClusters}
                    createCluster={createCluster}
                    deleteCluster={deleteCluster}
                    updateClusterStatus={updateClusterStatus}
                    fetchClusterDetails={fetchClusterDetails}
                    fetchClusterHealth={fetchClusterHealth}
                    fetchServiceStatus={fetchServiceStatus}
                    fetchNodes={fetchNodes}
                    createNode={createNode}
                    fetchNodeTelemetry={fetchNodeTelemetry}
                    updateNodeStatus={updateNodeStatus}
                    deleteNode={deleteNode}
                    assignNode={assignNode}
                    removeNode={removeNode}
                    transferNode={transferNode}
                    fetchUnassignedNodes={fetchUnassignedNodes}
                    fetchApps={fetchApps}
                    installApp={installApp}
                    fetchAppDetails={fetchAppDetails}
                    updateAppStatus={updateAppStatus}
                    deleteApp={deleteApp}
                    scaleApp={scaleApp}
                    restartApp={restartApp}
                    fetchServices={fetchServices}
                    fetchPods={fetchPods}
                    fetchPendingDevices={fetchPendingDevices}
                    approveDevice={approveDevice}
                    rejectDevice={rejectDevice}
                    setActiveCluster={setActiveCluster}
                    setActiveNamespace={setActiveNamespace}
                    connectTerminal={connectTerminal}
                    disconnectTerminal={disconnectTerminal}
                    executeCommand={executeCommand}
                    clearTerminalOutput={clearTerminalOutput}
                    addTerminalOutput={addTerminalOutput}
                    fetchRbacUsers={fetchRbacUsers}
                    inviteUser={inviteUser}
                    updateUserRole={updateUserRole}
                    deleteUser={deleteUser}
                    fetchRoles={fetchRoles}
                  />
                </TabsContent>
              );
            })}
          </div>
        </Tabs>
      </div>
    </div>
  );
} 